"use client"

import type { ReactNode } from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

/**
 * The frame every dialog in the ATS wears.
 *
 * `ui/dialog` had already been taken half-way to Graphite — the scrim, the card
 * ground, the 10px radius and the canvas's shadow are all there — but
 * everything above it was still stock: `p-6` all round, a `text-lg` title, a
 * `text-sm` description and `sm:` breakpoints in the footer. So each dialog set
 * its own sizes and they had drifted into as many dialects as there were
 * dialogs.
 *
 * This states them once: a header band with a hairline under it, a body that
 * scrolls on its own when the content is long, and a footer band that stacks
 * its buttons when there is no room for a row.
 *
 * The `@container` matters. A dialog is not the viewport — it is at most 460px
 * wide on a 1500px screen — so `sm:` inside one asks the window about a box
 * the window knows nothing about, and a two-column field row "responsive" on
 * `sm:` is two columns in a 400px dialog. Everything inside here measures the
 * dialog.
 */
export function DialogShell({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  wide,
  dismissible = true,
  className,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** A lucide icon at `size-4`, where the dialog names a kind of thing. */
  icon?: ReactNode
  children: ReactNode
  /** The action row: `PageAction tone="primary"` and `QuietAction size="md"`. */
  footer?: ReactNode
  /** For a dialog that holds a form wide enough to need two columns. */
  wide?: boolean
  /**
   * A dialog you cannot leave except through its own actions — picking the
   * organization you are signing in to. It withholds the X, and ignores the
   * escape key and a click on the scrim, because there is nothing behind it to
   * go back to.
   */
  dismissible?: boolean
  className?: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={dismissible}
        onInteractOutside={dismissible ? undefined : (event) => event.preventDefault()}
        onEscapeKeyDown={dismissible ? undefined : (event) => event.preventDefault()}
        className={cn(
          "@container w-[calc(100vw-2rem)] gap-0 p-0",
          wide ? "max-w-2xl" : "max-w-[460px]",
          className,
        )}
      >
        <DialogHeader className="flex-none space-y-1 border-b px-4 py-3.5 text-left @sm:px-5">
          <DialogTitle className="flex items-center gap-2 text-[14px] font-semibold leading-[1.3] tracking-[-0.01em]">
            {icon}
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-[12px] leading-[1.5] text-muted-foreground">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* The body scrolls, not the page behind it and not the whole dialog:
            the header stays legible and the actions stay reachable. */}
        <div className="max-h-[70vh] min-h-0 overflow-y-auto px-4 py-4 @sm:px-5">
          {children}
        </div>

        {footer && (
          <div className="flex flex-none flex-col-reverse gap-2 border-t px-4 py-3 @sm:flex-row @sm:items-center @sm:justify-end @sm:px-5">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}


/** The size every control inside a dialog takes, so they line up. */
export const DIALOG_CONTROL = "h-9 text-[12.5px]"
